import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useEntityStore } from '@/lib/sync/entity-store';
import type { RecordingMeta } from '@/lib/recordings/types/types';

/**
 * Resolves the recording that belongs to a specific audio surface, not merely
 * the document's most recent one — required once a document can hold several
 * recordings (design §3.6).
 *
 * Resolution order:
 * 1. `recordingId`, when set — an exact lookup, validated against both
 *    `entityId` and (when the recording carries one) `surfaceId`.
 *    `recordingId` comes from the client-written manifest, so a stale entry
 *    must not render another recording's transcript.
 * 2. Otherwise match `recording.surfaceId === surfaceId` (M1 persists
 *    `surface_id` server-side). This recovers the binding from server-owned
 *    state when the client's live-only bind effect never landed on the
 *    manifest entry (M5 bug: `surface-mount.tsx`'s bind effect only runs
 *    while a recording is LIVE; once it ends, a surface that missed the
 *    window would otherwise stay unbound forever).
 * 3. Otherwise `null`. A surface with no matching recording renders empty —
 *    there is no per-document fallback (design §6.1). An earlier version of
 *    this resolver fell back to the document's most-recent recording for
 *    manifest entries written before M1; there is no such data (pre-launch,
 *    production held zero briefing rows), so that branch was dead weight
 *    carried only to serve data that does not exist.
 */
export function useRecordingForSurface(
  entityId: string | null | undefined,
  surfaceId: string | null | undefined,
  recordingId: string | null,
): RecordingMeta | null {
  const recordings = useEntityStore(
    useShallow((s) => Array.from(s.recordings.values())),
  );

  return useMemo(() => {
    if (recordingId) {
      return (
        recordings.find(
          (r) => r != null
            && r.id === recordingId
            && r.entityId === entityId
            && (r.surfaceId == null || r.surfaceId === surfaceId),
        ) ?? null
      );
    }
    if (surfaceId) {
      return (
        recordings.find(
          (r) => r != null && r.entityId === entityId && r.surfaceId === surfaceId,
        ) ?? null
      );
    }
    return null;
  }, [recordings, recordingId, surfaceId, entityId]);
}
